/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Injectable } from '@angular/core';
import { AbstractCanvasConnection } from './AbstractCanvasConnection';
import { ActiveLine } from '../../../app-data/models/ActiveLine';
import { CameraService } from './camera.service';
import { Observable, Subscription } from 'rxjs';
import { ActiveLineService } from './active-line.service';
import { dateCache, DefaultSplitDataRenderType, guessDateFromX, guessXFromDate } from '@touchplan/common';
import { debounce, map } from 'rxjs/operators';
import { renderQueue } from '../../../plain-javascript/services/RenderQueue';
import { drop } from '../../../plain-javascript/services/rxjs-internal-extensions';

/**
 * Interface defining the structure of a column in the timeline.
 * Each column represents a single day in the timeline.
 */
export interface IActiveColumn {
	activeDay: string; // The date in YYYY-MM-DD format
	dayDate: string; // The day part (e.g., '01', '02')
	minDate: string; // Abbreviated day of week (e.g., 'Mon', 'Tue')
	yearDate: string; // Year info for timeline grouping (part of new timeline feature set)
	finishX: number; // End X position of the column
	startX: number; // Start X position of the column
	fullDate?: string; // Full date representation for week start (e.g., 'Jan 01')
}

/**
 * Interface for overriding the active line position and date
 * This came from the old active column factory
 */
interface ActiveLineOverride {
	date: string;
	x: number;
}

/**
 * Configuration settings for the ActiveColumnService
 */
const settings = {
	columnWidth: 145, // 145 came from old code and represents the width of a single Task-type ticket.
	zoomStages: [33, 13, 9, 6, 5, 4, 3, 2, 1, 0.7],
	// Zoom breakpoints for level of detail. Values came from old code, otherwise no clue _why_ these specific values were chosen.
	fullDateFormat: `MMM DD`, // Special date that appears for every SUNDAY. Why Sunday? Probably a production decision circa 2015
	minDateFormat: `ddd`, // Date format for day (`Mon`, `Tue`, `Wed`, etc)
	dayDateFormat: `DD`, // Day number in month (28, 29, 30, 01, etc)
	yearDateFormat: `YYYY`, // The year for this date. Part of the new timeline feature
	fullDateComparator: `Sun`, // If a column has this as a min date, then it will set the full date property.
	debounceRate: 60, // Controls how much to wait before re-calculating columns. 60 = one update per 60 frames
};

/**
 * Service responsible for generating and managing date-based columns in the timeline.
 * The ActiveColumnService creates columns representing days in the timeline, with each
 * column positioned based on the active line (right boundary) and extending leftward.
 * It handles generating the appropriate number of columns based on the viewport,
 * culling columns when zoomed out to prevent overcrowding, and provides utilities
 * for converting between dates and x-coordinates.
 *
 * How does it work:
 * - The column generation will start by converting a known X-position to a Date.
 * - In our case, the activeLineX will be used to calculated the first column date.
 * - Every subsequent column will then be generated by offsetting this date, factor in the zoom scale into the offset
 * - This then gives us the adjacent date (moving leftwards)
 *
 * We then generate columns to the left until we hit the left-edge of the screen edge.
 */
@Injectable({ providedIn: 'root' })
export class ActiveColumnService extends AbstractCanvasConnection<IActiveColumn[]> {
	/**
	 * visibleColumns$ observable is overridden from the AbstractCanvasConnection so we can apply culling to the columns
	 */
	public visibleColumns$: Observable<IActiveColumn[] | null> = this._changed$.pipe(
		map((data) => this.cullColumns(data as IActiveColumn[]))
	);
	/**
	 * Reference to the current active line
	 * Cached to avoid recalculating when not necessary
	 */
	private _cachedActiveLine!: ActiveLine | null;
	/**
	 * Subscription to camera service changes
	 * Separate from _subscription to handle both camera and active line independently
	 */
	private _cameraSubscription!: Subscription | null;
	/**
	 * Cache for columns by date string to improve performance
	 * Prevents regenerating column data for dates we've already calculated
	 */
	private _columnCache: Map<string, IActiveColumn> = new Map();
	/**
	 * Sorted list of all of our generated columns so we do not need to create a new array each tick.
	 * @private
	 */
	private _columns: IActiveColumn[] = [];
	/**
	 * The start index for culling the columns (represents the left edge of the screen)
	 * @private
	 */
	private _startIndex: number = 0;
	/**
	 * The max index of culling the columns (represents the right-edge of the screen or active line)
	 * @private
	 */
	private _maxIndex: number = 0;
	/**
	 * Variable used for tracking culled columns. Without this we are getting different culling results compared to legacy code
	 * @private
	 */
	private _startCounter: number = 0;
	/**
	 * It is possible that the active line data will be emitted before the canvas is loaded. This will then trigger a purge
	 * of the columns. However, at this point the camera is not yet ready to handle the worldToScreen calculations, and the app will error.
	 * This _isReady flag is a hack so we never allow a purge + update of the columns until the camera has emitted at least one event.
	 * In a better world, we would merge the `stateChange$` observable with our activeLine$ observable, but doing so requires
	 * adding `planState` as a dependency to this.
	 * Alternatively, we could combineLatest the camera changed$ event with the active line event, but that adds complexity
	 * to figure out which observable emitted so we can do a different code path.
	 * So...doing a flag to simplify all of that.
	 * @private
	 */
	private _isReady: boolean = false;

	/**
	 * Creates an instance of ActiveColumnService.
	 * @param _cameraService - Service that provides access to the canvas Camera.ts
	 * @param _activeLineService - Service that provides active line data
	 */
	constructor(private _cameraService: CameraService, private _activeLineService: ActiveLineService) {
		super();
	}

	/**
	 * Helper method to return the active space column width
	 */
	public get columnWidth(): number {
		return settings.columnWidth;
	}
	/**
	 * Sets up subscriptions to camera and active line changes.
	 * This method is called in plan.component when the plan is created.
	 */
	public override registerPlanData(): void {
		this.clearPreviousSubscriber();
		// We only want to calculate the columns AT MOST once per X ticks. Otherwise we could  be generating columns multiple
		// times in the same frame, which would all be wasted time :sadge:
		this._cameraSubscription = this._cameraService.changed$
			.pipe(debounce(() => renderQueue.ob$.pipe(drop(settings.debounceRate))))
			.subscribe((viewportEvent) => {
				this._isReady = true;
				this.updateColumns();
			});

		this._subscription = this._activeLineService.changed$.subscribe((activeLine: ActiveLine | null) => {
			if (!this._isReady) {
				return;
			}
			this._cachedActiveLine = activeLine;
			this.purgeAndUpdate(activeLine);
		});
	}

	/**
	 * Helper method to determine if the current scaled column width is wide enough to draw the day text
	 */
	public shouldDrawInner(): boolean {
		return this._isReady && settings.columnWidth * this._cameraService.scale.x > settings.zoomStages[0];
	}

	/**
	 * Helper method to calculate the X coordinate for a given date on the active side.
	 * @param date
	 * @param dayOffset
	 */
	public getXFromDate(date: string, dayOffset: number = 0): number {
		if (
			!this._cachedActiveLine ||
			!this._cachedActiveLine.view.activeLineDate ||
			this._cachedActiveLine.view.activeLineX === undefined
		) {
			Logging.error(`Unable to find X coordinate because there is no active line.`);
			return Infinity;
		}
		return guessXFromDate(
			date,
			dayOffset,
			this._cachedActiveLine.view.activeLineDate,
			this._cachedActiveLine.view.activeLineX
		);
	}

	/**
	 * Helper method to calculate the date for a given X coordinate
	 * @param x
	 * @param activeLineOffset
	 */
	public getDateFromX(x: number, activeLineOffset: number = 0): string {
		if (
			!this._cachedActiveLine ||
			!this._cachedActiveLine.view.activeLineDate ||
			this._cachedActiveLine.view.activeLineX === undefined
		) {
			return dateCache.todayString;
		}
		return guessDateFromX(
			x,
			this._cachedActiveLine.view.activeLineX,
			activeLineOffset,
			this._cachedActiveLine.view.activeLineDate
		);
	}

	/**
	 * Clears the column cache and immediately calculates new columns.
	 * We need to completely clear our cache here because all of our previously generated column data may be stale
	 * once the active line position changes.
	 * @param activeLine - The new active line state
	 * @private
	 */
	private purgeAndUpdate(activeLine: ActiveLine | null): void {
		if (!activeLine) {
			this._changed$.next(null);
			return;
		}

		this._columnCache.clear();
		this._columns = [];
		this.updateColumns();
	}

	/**
	 * Generates columns based on current viewport and active line.
	 * @private
	 */
	private updateColumns(): void {
		if (
			!this._cachedActiveLine ||
			this._cachedActiveLine.view.activeLineX === undefined ||
			!this._cachedActiveLine.view.activeLineDate
		) {
			this._changed$.next(null);
			return;
		}

		// Stop calculating active columns when active line is in edit view.
		// Otherwise if we drag to the pull side, we will generate new active columns before the operation was persisted
		if (this._cachedActiveLine.currentRenderType === DefaultSplitDataRenderType.edit) {
			return;
		}

		// Calculate how many columns to generate
		// Start from active line and work leftward
		const startX: number = this._cachedActiveLine.view.activeLineX;
		const leftBoundaryX: number = this._cameraService.convertScreenToWorldX(0);
		const columnCount = Math.ceil((startX - leftBoundaryX) / settings.columnWidth) + 1;

		for (let i = 0; i < this._columns.length; i++) {
			const screenX: number = this._cameraService.convertWorldXToScreen(this._columns[i].finishX);
			if (screenX < this._cameraService.screen.width) {
				this._startIndex = i;
				break;
			}
		}

		// Generate columns
		let innerMax: number = 0;
		for (let i = 0; i < columnCount; i++) {
			if (!this._columns[i]) {
				const activeDay: string = dateCache.substractDays(this._cachedActiveLine.view.activeLineDate, i);
				const column: IActiveColumn = this.generateColumn(activeDay, i, startX, settings.columnWidth);

				this._columns.push(column);
				continue;
			}

			if (this._cameraService.convertWorldXToScreen(this._columns[i].finishX) < 0 && !this._maxIndex) {
				innerMax = i;
			}
		}
		this._maxIndex = innerMax || this._columns.length;

		this._changed$.next(this._columns);
	}

	/**
	 * This applies the spreading algorithm/culling on the generated columns.
	 * This is the same as the old active column service, with very minor adjustments.
	 * @param columns
	 * @private
	 */
	private cullColumns(columns: IActiveColumn[]): IActiveColumn[] {
		const min: number = this._startIndex;
		const max: number = this._maxIndex;

		if (!columns || !columns.length) {
			return [];
		}

		const stageKey: number = this.getZoomLevel();

		if (stageKey === settings.zoomStages.length) {
			return [];
		}

		const visibleColumns: IActiveColumn[] = [];
		let counter: number = 0;

		if (!stageKey) {
			for (let i = min; i < max; i++) {
				visibleColumns.push(columns[i]);
			}
			return visibleColumns;
		}

		for (let i = 0; i < columns.length; i++) {
			if (columns[i].fullDate) {
				counter++;
				if (counter >= stageKey) {
					if (i >= min && i < max) {
						visibleColumns.push(columns[i]);
					}
					counter = 0;
				}
			}
		}

		if (visibleColumns.length === 0 && columns.length > 0) {
			for (let i = min; i < max && i < columns.length; i++) {
				if (columns[i]) {
					visibleColumns.push(columns[i]);
					break;
				}
			}
		}

		return visibleColumns;
	}

	/**
	 * getZoomLevel this returns the zoom stage for column culling
	 * This is the exact same as the old active column algorithm.
	 * @private
	 */
	private getZoomLevel(): number {
		const scale: number = this._cameraService.scale.x;
		const scaledWidth: number = settings.columnWidth * scale;
		let stageKey;
		for (stageKey = 0; stageKey < settings.zoomStages.length; stageKey++) {
			if (scaledWidth > settings.zoomStages[stageKey]) {
				break;
			}
		}
		return stageKey;
	}

	/**
	 * Cleans up any existing subscription before setting up a new one.
	 */
	protected override clearPreviousSubscriber(): void {
		if (!this._subscription) {
			return;
		}

		this._columnCache.clear();
		this._subscription.unsubscribe();
		this._cameraSubscription?.unsubscribe();
		this._subscription = null;
		this._cameraSubscription = null;
		this._cachedActiveLine = null;
		this._isReady = false;
	}

	/**
	 * Generates a column for a specific date and position.
	 * Creates and caches a new column if it doesn't exist yet.
	 * @param activeDay - Date string for this column
	 * @param index - Index of the column (0 is at active line, increasing leftward)
	 * @param startX - X position of the active line
	 * @param columnWidth - Width of each column
	 * @returns Column object
	 * @private
	 */
	private generateColumn(
		activeDay: string,
		index: number,
		startX: number,
		columnWidth: number
	): IActiveColumn {
		if (this._columnCache.has(activeDay)) {
			return this._columnCache.get(activeDay) as IActiveColumn;
		}
		const minDate: string = dateCache.getFormat(activeDay, settings.minDateFormat);
		const dayDate: string = dateCache.getFormat(activeDay, settings.dayDateFormat);
		const yearDate: string = dateCache.getFormat(activeDay, settings.yearDateFormat);

		const column: IActiveColumn = {
			finishX: startX - index * columnWidth,
			startX: startX - index * columnWidth - columnWidth,
			activeDay,
			minDate,
			dayDate,
			yearDate,
		};

		if (column.minDate === settings.fullDateComparator) {
			column.fullDate = dateCache.getFormat(activeDay, settings.fullDateFormat);
		}

		this._columnCache.set(activeDay, column);

		return column;
	}
}
